import { ClearSession } from '@/redux/actions/authActions';
import { getCurrentCustomer } from '@/redux/actions/customerActions';
import { setLocalStorageData } from '@/utils/LocalStorage';
import { clearCookies } from '@/utils/cookies';
import { left, marginLeft, right } from '@/utils/directions';
import {
	colorBackground,
	colorBorder,
	colorHover,
	colorText,
} from '@/utils/palette';
import { useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { TopbarBack, TopbarDownIcon } from './BasicTopbarComponents';
import NavbarProfilePicture from './NavbarProfilePicture';
import TopbarMenuLink, { TopbarLink } from './TopbarMenuLink';

import { ResetIndexedDb } from '@/utils/ResetIndexedDb';
import useUrlQueryParams from '@/utils/hooks/useUrlQueryParams';
import NotificationIcon from './Notification/NotificationBell';
const TopbarProfile = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { encryptQueryParams } = useUrlQueryParams();
	const [isCollapsed, setIsCollapsed] = useState(false);
	const user = useSelector(state => state?.auth?.currentUser);
	const { fullname, userID, customerName } = useSelector(
		state => state?.auth?.currentUser,
	);
	const permissions = useSelector(
		state => state?.permissionsStorage?.allModulePermissions?.customers,
	);

	const encryptedUserParams = encryptQueryParams({ userID });
	const encryptedComapanyParams = encryptQueryParams({
		customerID: user?.customerID,
	});

	const toggleCollapse = () => {
		setIsCollapsed(!isCollapsed);
	};

	const SubmitHandler = async () => {
		ResetIndexedDb('TopProfile');
		dispatch(ClearSession());
		clearCookies();
		sessionStorage.clear();
		localStorage.clear();
		navigate('/login');
	};

	return (
		<TopbarProfileWrap>
			{import.meta.env.VITE_REACT_APP_NODE_ENV !== 'localhost' ? (
				<NotificationIcon />
			) : null}
			<TopbarAvatarButton type="button" onClick={toggleCollapse}>
				<NavbarProfilePicture user={user} />
				<TopbarAvatarName>{fullname}</TopbarAvatarName>
				<TopbarDownIcon />
			</TopbarAvatarButton>
			{isCollapsed && (
				<TopbarBack
					type="button"
					aria-label="button collapse"
					onClick={toggleCollapse}
				/>
			)}
			<Collapse in={isCollapsed}>
				<TopbarMenuWrap>
					<TopbarMenu>
						<TopbarMenuLink
							title="Profile Settings"
							icon="user"
							path={`/profile?${encryptedUserParams}`}
							onClick={() => {
								setLocalStorageData('dataOwner', true);
								toggleCollapse();
							}}
						/>
						{permissions?.read ? (
							<TopbarMenuLink
								title="Company Settings"
								icon="apartment"
								path={`/company/${customerName}?${encryptedComapanyParams}`}
								onClick={() => {
									localStorage.setItem('currentCustomerID', user?.customerID);
									dispatch(getCurrentCustomer());
									toggleCollapse();
								}}
							/>
						) : null}
						<TopbarMenuLink
							title="Contact Us"
							icon="flag"
							path="/contactUs/"
							onClick={() => {
								setLocalStorageData('currentCustomerID', user?.customerID);
								toggleCollapse();
							}}
						/>
						<TopbarMenuLink
							title="Documentation"
							icon="cog"
							path="https://www.engineering-docs.stninc.com/"
							target="_blank" // This opens the link in a new window
						/>
						<TopbarMenuDivider />
						<TopbarMenuLink
							title="Log Out"
							icon="exit"
							onClick={SubmitHandler}
						/>
					</TopbarMenu>
				</TopbarMenuWrap>
			</Collapse>
		</TopbarProfileWrap>
	);
};

export default TopbarProfile;

// region STYLES

export const TopbarProfileWrap = styled.div`
	position: relative;
	margin-bottom: 0;
	height: 60px;
	${marginLeft}: 0;
	display: flex;

	@media screen and (max-width: 576px) {
		margin: inherit;
	}

	@media screen and (max-width: 320px) {
		margin: auto 0;
	}
`;

const TopbarAvatarButton = styled.button`
	height: 100%;
	display: flex;
	cursor: pointer;
	position: relative;
	border-radius: 0;
	border: none;
	transition: all 0.3s;
	box-shadow: none;
	padding: 0 15px;
	background-color: transparent;

	&:hover,
	&:focus,
	&:active,
	&:focus:active {
		background-color: ${colorHover};
	}

	&:focus {
		outline: none;
	}

	&:before {
		display: none;
	}
`;

export const TopbarAvatarName = styled.p`
	margin: auto 0;
	font-size: 13px;
	line-height: 18px;
	font-weight: 400;
	display: none;
	${marginLeft}: 5px;
	color: ${colorText};

	@media screen and (min-width: 480px) {
		display: block;
	}
`;

const TopbarMenuWrap = styled.div`
	z-index: 101;
	position: absolute;
	width: 100%;
	min-width: 210px;
	${right}: 0;
	top: 50px;

	@media screen and (max-width: 320px) {
		${right}: -50px;
	}
`;

const TopbarMenu = styled.div`
	width: 200px;
	border-radius: 0;
	border: none;
	padding: 15px 0;
	box-shadow: 0 2px 15px 0 rgba(0, 0, 0, 0.05);
	margin-top: 0;
	background: ${colorBackground};

	button {
		padding: 0;

		&:hover {
			background-color: ${colorHover};
		}

		&${TopbarLink} {
			background-color: transparent;
			border: none;
			padding: 9px 20px;
		}
	}

	*:focus {
		outline: none;
	}

	@media screen and (min-width: 480px) {
		width: 100%;
		${left}: 0 !important;
	}
`;

const TopbarMenuDivider = styled.div`
	margin: 15px 0;
	border-top: 1px solid ${colorBorder};
`;

// endregion
